import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import {
  BrainCircuit, Radar, LineChart, TrendingUp, TrendingDown, Minus,
  Check, UserCheck, PlugZap, ShieldCheck, MessageSquare, ChevronDown,
  Gauge, Zap, FileUp, History, CloudSun, Receipt, CalendarRange, Phone, Database,
} from "lucide-react";

const C = {
  bg: "#0A0B0E", surface: "#12141C", elevated: "#1A1D28",
  wine: "#58111A", wineLight: "#7D1826", gold: "#E5A93C",
  text: "#F8FAFC", muted: "#94A3B8", border: "rgba(255,255,255,0.08)",
};
const SERIF = { fontFamily: "'Cormorant Garamond', Playfair Display, serif" };
const HERO_IMG = "https://images.unsplash.com/photo-1695638720110-72b396c0915f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const TUTOR_IMG = "https://images.unsplash.com/photo-1523699289804-55347c09047d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const occColor = (v) => {
  const hue = Math.round(140 - (140 * Math.max(0, Math.min(100, v))) / 100);
  return `hsl(${hue}, 66%, 42%)`;
};

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6, delay: d },
});

// ---------- UI preview mock-ups (stile app, dati generici) ----------
function PriceCardPreview() {
  return (
    <div className="rounded-2xl p-5 border" style={{ background: C.elevated, borderColor: C.border }} data-testid="preview-price-card">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] uppercase tracking-widest" style={{ color: C.gold }}>Consiglio Prezzo · Sab 4 Ott</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: "rgba(16,185,129,0.15)", color: "#10B981" }}>Aumenta +8%</span>
      </div>
      <div className="flex items-end gap-3">
        <span className="text-4xl font-extrabold" style={{ color: C.text }}>€142</span>
        <span className="text-sm mb-1" style={{ color: C.muted }}>netto €108</span>
      </div>
      <div className="mt-3 text-sm leading-relaxed" style={{ color: C.muted }}>
        <span style={{ color: C.gold }}>■</span> Domanda alta nel weekend, 3 competitor già sold-out e ritmo prenotazioni sopra la media: alzo la tariffa mantenendo il vantaggio.
      </div>
      <div className="mt-4 flex gap-2">
        <div className="flex-1 rounded-lg py-2 text-center text-xs font-semibold" style={{ background: C.wine, color: C.text }}>Applica</div>
        <div className="rounded-lg py-2 px-3 text-center text-xs" style={{ background: C.surface, color: C.muted, border: `1px solid ${C.border}` }}>Dettaglio</div>
      </div>
    </div>
  );
}

function HeatmapPreview() {
  const comps = ["Struttura A", "Struttura B", "Struttura C", "Struttura D"];
  const rows = [
    [30, 45, 60, 80, 92, 100, 70, 55, 40, 35],
    [50, 55, 65, 72, 88, 95, 60, 48, 42, 38],
    [20, 30, 44, 61, 79, 90, 66, 50, 33, 28],
    [60, 62, 70, 85, 96, 100, 74, 58, 45, 40],
  ];
  const days = ["28", "29", "30", "1", "2", "3", "4", "5", "6", "7"];
  return (
    <div className="rounded-2xl p-5 border overflow-hidden" style={{ background: C.elevated, borderColor: C.border }} data-testid="preview-heatmap">
      <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: C.gold }}>Occupazione competitor · 6 mesi</div>
      <div className="grid" style={{ gridTemplateColumns: `84px repeat(${days.length}, 1fr)`, gap: 4 }}>
        <div />
        {days.map((d, i) => <div key={i} className="text-[10px] text-center" style={{ color: C.muted }}>{d}</div>)}
        {comps.map((c, ri) => (
          <React.Fragment key={ri}>
            <div className="text-[11px] truncate flex items-center" style={{ color: C.text }}>{c}</div>
            {rows[ri].map((v, ci) => (
              <div key={ci} className="h-6 rounded flex items-center justify-center text-[9px] font-bold text-white"
                style={{ background: occColor(v) }}>{v}</div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2 text-[10px]" style={{ color: C.muted }}>
        Bassa
        <span className="inline-block h-2.5 w-28 rounded" style={{ background: "linear-gradient(90deg, hsl(140,66%,42%), hsl(70,66%,42%), hsl(0,66%,42%))" }} />
        Alta
      </div>
    </div>
  );
}

function DecisionPreview() {
  const items = [
    { Icon: TrendingUp, c: "#10B981", t: "Aumenta tariffa", d: "Sab 4 Ott · domanda alta", tag: "+8%" },
    { Icon: Zap, c: C.gold, t: "Azione last-minute", d: "Mar 30 Set · 4 camere libere", tag: "-12%" },
    { Icon: TrendingDown, c: "#EF4444", t: "Riduci prezzo", d: "Lun 6 Ott · ritmo lento", tag: "-6%" },
    { Icon: Minus, c: C.muted, t: "Mantieni", d: "Dom 5 Ott · in linea", tag: "=" },
  ];
  return (
    <div className="rounded-2xl p-5 border" style={{ background: C.elevated, borderColor: C.border }} data-testid="preview-decision">
      <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: C.gold }}>Centro decisionale</div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={{ background: C.surface }}>
            <it.Icon className="w-4 h-4 shrink-0" style={{ color: it.c }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold" style={{ color: C.text }}>{it.t}</div>
              <div className="text-[11px]" style={{ color: C.muted }}>{it.d}</div>
            </div>
            <span className="text-xs font-bold" style={{ color: it.c }}>{it.tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CalendarPreview() {
  const cells = [
    { d: "Lun 29", p: 96, dm: "green" }, { d: "Mar 30", p: 104, dm: "green" },
    { d: "Mer 1", p: 118, dm: "yellow" }, { d: "Gio 2", p: 122, dm: "yellow" },
    { d: "Ven 3", p: 138, dm: "red" }, { d: "Sab 4", p: 142, dm: "red" }, { d: "Dom 5", p: 120, dm: "yellow" },
  ];
  const dmc = { green: "#10B981", yellow: "#F59E0B", red: "#EF4444" };
  return (
    <div className="rounded-2xl p-5 border" style={{ background: C.elevated, borderColor: C.border }} data-testid="preview-calendar">
      <div className="text-[11px] uppercase tracking-widest mb-3" style={{ color: C.gold }}>Calendario intelligente · prezzi consigliati</div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((c, i) => (
          <div key={i} className="rounded-lg py-2.5 text-center" style={{ background: C.surface, border: `1px solid ${C.border}` }}>
            <div className="text-[10px]" style={{ color: C.muted }}>{c.d}</div>
            <div className="text-sm font-bold my-1" style={{ color: C.text }}>€{c.p}</div>
            <div className="mx-auto h-1.5 w-1.5 rounded-full" style={{ background: dmc[c.dm] }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- Sections ----------
const PILLARS = [
  { Icon: BrainCircuit, t: "Prezzi dinamici intelligenti", d: "Mai più una camera svenduta o vuota. L'AI suggerisce la tariffa perfetta per ogni singola notte, e ti spiega il perché." },
  { Icon: Radar, t: "Intelligence competitor a 6 mesi", d: "Monitora prezzi, disponibilità e occupazione dei competitor attorno a te con heatmap intuitive." },
  { Icon: UserCheck, t: "Tutor dedicato incluso", d: "Un esperto di Revenue al tuo fianco 1 volta al mese per rivedere la strategia e massimizzare i ricavi." },
];

const FEATURES = [
  { Icon: BrainCircuit, t: "Motore AI & trasparenza", d: "Ogni prezzo consigliato è accompagnato da una spiegazione in linguaggio naturale, come un vero Revenue Manager al tuo fianco." },
  { Icon: Radar, t: "Analisi competitor", d: "Prezzi, disponibilità e heatmap occupazione dei competitor attorno a te, fino a 6 mesi avanti." },
  { Icon: CloudSun, t: "Meteo & eventi", d: "Considera previsioni meteo ed eventi locali (fiere, concerti, festività) per anticipare i picchi di domanda." },
  { Icon: TrendingUp, t: "Trend di mercato", d: "Intercetta l'andamento del mercato della tua zona e adatta le tariffe alle fasi di crescita o calo." },
  { Icon: Database, t: "Il tuo storico", d: "Impara dai tuoi dati storici di vendita e occupazione per consigli sempre più su misura per la tua struttura." },
  { Icon: LineChart, t: "Previsione domanda a 90 giorni", d: "Prezzo consigliato, minimo e massimo per ogni giorno, tarati sull'andamento reale del mercato." },
  { Icon: Receipt, t: "Costi & tariffa base", d: "Inserisci i tuoi costi e ottieni la tariffa base minima: l'AI ti avvisa se un prezzo scende sotto il tuo margine." },
  { Icon: Gauge, t: "Centro decisionale strategico", d: "Azioni chiare e immediate: aumenta, last-minute, riduci o mantieni, in base al ritmo delle prenotazioni." },
  { Icon: History, t: "Storico consigli & apprendimento", d: "L'algoritmo valuta l'esito delle vendite (\"ha funzionato\", \"margine perso\") e migliora continuamente." },
  { Icon: FileUp, t: "Importazione dati PMS", d: "Carica i file del tuo PMS (es. Krossbooking) per tracciare occupazione reale e ritmo prenotazioni in un attimo." },
];

const SPOTLIGHT = [
  { Icon: Phone, t: "Preventivo al telefono", d: "Un cliente ti chiama per un preventivo? In pochi secondi ottieni il prezzo giusto da proporre, calcolato sulla tua tariffa pubblicata e sui prezzi dei competitor. Chiudi la trattativa senza mai svenderti." },
  { Icon: CalendarRange, t: "Prezzi di un anno, anche se hai appena aperto", d: "Nessuno storico? Nessun problema. Imposti comunque i prezzi dei prossimi 12 mesi grazie ai dati di mercato, ai competitor della tua zona, a meteo ed eventi: parti subito con tariffe sensate." },
];

const FAQS = [
  { q: "Come funziona l'offerta a 79€/mese bloccata a vita?", a: "I primi 50 clienti hanno il prezzo di 79€/mese che resta invariato per sempre, anche quando aggiungeremo l'aggiornamento automatico dei prezzi sul PMS. Il prezzo standard futuro sarà 129€/mese." },
  { q: "Posso disdire quando voglio?", a: "Assolutamente sì. L'abbonamento è mensile e senza vincoli di durata: puoi uscire in qualsiasi momento, senza penali." },
  { q: "Cosa include il tutor dedicato?", a: "Una volta al mese concordi una sessione individuale con un nostro esperto di Revenue Management, che analizza le tue metriche e ti aiuta a impostare le strategie migliori. In più hai l'assistenza online continua." },
  { q: "Quali strutture possono usarlo?", a: "Revenue AI è pensato per B&B, case vacanza, affittacamere e piccoli hotel che vogliono ottimizzare occupazione e profitti." },
  { q: "I miei dati sono al sicuro?", a: "Sì, la riservatezza è una priorità: i dati della tua struttura sono protetti secondo le normative GDPR europee." },
];

function FaqItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border overflow-hidden" style={{ borderColor: C.border, background: C.surface }}>
      <button data-testid={`faq-trigger-${i}`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-semibold" style={{ color: C.text }}>{q}</span>
        <ChevronDown className="w-5 h-5 shrink-0 transition-transform" style={{ color: C.gold, transform: open ? "rotate(180deg)" : "none" }} />
      </button>
      {open && <div className="px-5 pb-4 text-sm leading-relaxed" style={{ color: C.muted }} data-testid={`faq-content-${i}`}>{a}</div>}
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const { user } = useApp();
  const go = () => nav(user ? "/dashboard" : "/request-access");

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} className="min-h-screen" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl" style={{ background: "rgba(10,11,14,0.72)", borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Revenue AI" className="w-9 h-9 object-contain" draggable="false" />
            <span className="text-xl font-bold" style={SERIF}>Revenue<span style={{ color: C.gold }}>AI</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-7 text-sm" style={{ color: C.muted }}>
            <a href="#funzionalita" className="hover:text-white transition-colors">Funzionalità</a>
            <a href="#intelligence" className="hover:text-white transition-colors">Intelligence</a>
            <a href="#tutor" className="hover:text-white transition-colors">Tutor</a>
            <a href="#prezzi" className="hover:text-white transition-colors">Prezzi</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <button data-testid="nav-login-link" onClick={() => nav("/login")} className="hidden sm:inline text-sm px-3 py-2 rounded-lg hover:text-white transition-colors" style={{ color: C.muted }}>Accedi</button>
            <Button data-testid="nav-request-access-btn" onClick={go} className="rounded-full px-5" style={{ background: C.wine }}>Richiedi accesso</Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(88,17,26,0.85) 0%, rgba(10,11,14,0.9) 55%, rgba(10,11,14,0.98) 100%)" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.div {...fade(0)} className="flex flex-wrap gap-2 mb-6">
              <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: "rgba(125,24,38,0.35)", color: C.gold, border: `1px solid rgba(229,169,60,0.35)` }}>★ Offerta limitata ai primi 50</span>
              <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ background: "rgba(255,255,255,0.06)", color: C.muted }}>Senza vincoli · disdici quando vuoi</span>
            </motion.div>
            <motion.h1 {...fade(0.05)} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight" style={SERIF}>
              Ottimizza i prezzi del tuo <span style={{ color: C.gold }}>B&B</span> con l'Intelligenza Artificiale
            </motion.h1>
            <motion.p {...fade(0.12)} className="mt-6 text-lg leading-relaxed max-w-xl" style={{ color: C.muted }}>
              Il copilota di Revenue Management che analizza i competitor, prevede la domanda e ti guida giorno per giorno verso il massimo profitto — spiegandoti ogni decisione.
            </motion.p>
            <motion.div {...fade(0.2)} className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-cta-button" size="lg" onClick={go} className="rounded-full text-base px-8" style={{ background: C.gold, color: "#1A1204" }}>Richiedi accesso subito</Button>
              <Button data-testid="hero-secondary-btn" size="lg" variant="outline" onClick={() => nav("/login")} className="rounded-full text-base px-8 bg-transparent" style={{ borderColor: C.border, color: C.text }}>Ho già un account</Button>
            </motion.div>
            <motion.div {...fade(0.28)} className="mt-6 flex flex-wrap gap-5 text-sm" style={{ color: C.muted }}>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: C.gold }} />Tutor dedicato ogni mese</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: C.gold }} />Assistenza online</span>
            </motion.div>
          </div>
          <motion.div {...fade(0.25)} className="space-y-4">
            <PriceCardPreview />
            <DecisionPreview />
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section id="funzionalita" className="max-w-7xl mx-auto px-6 py-20">
        <motion.h2 {...fade(0)} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center" style={SERIF}>Smetti di tirare a indovinare i prezzi</motion.h2>
        <motion.p {...fade(0.08)} className="mt-4 text-center max-w-2xl mx-auto" style={{ color: C.muted }}>Revenue AI trasforma dati di mercato, competitor e domanda in decisioni chiare per la tua struttura.</motion.p>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => (
            <motion.div key={i} {...fade(i * 0.1)} className="rounded-2xl p-7 border" style={{ background: C.surface, borderColor: C.border }} data-testid={`pillar-${i}`}>
              <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "rgba(125,24,38,0.25)" }}><p.Icon className="w-6 h-6" style={{ color: C.gold }} /></span>
              <h3 className="text-xl font-semibold mb-2">{p.t}</h3>
              <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Intelligence showcase with previews */}
      <section id="intelligence" className="py-20" style={{ background: C.surface }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade(0)}>
            <span className="text-xs uppercase tracking-widest" style={{ color: C.gold }}>Intelligence competitor</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3" style={SERIF}>Guarda il mercato prima degli altri</h2>
            <p className="mt-4 leading-relaxed" style={{ color: C.muted }}>La heatmap ti mostra l'occupazione dei tuoi 5 competitor selezionati per ogni data, fino a 6 mesi avanti: capisci al volo dove c'è alta domanda e imposti la tariffa giusta.</p>
            <ul className="mt-6 space-y-2 text-sm" style={{ color: C.text }}>
              {["Prezzi e disponibilità in tempo reale", "Occupazione stimata per ogni giorno", "Aggiornamento automatico programmato"].map((x, i) => (
                <li key={i} className="flex items-center gap-2"><Check className="w-4 h-4" style={{ color: C.gold }} />{x}</li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fade(0.15)}><HeatmapPreview /></motion.div>
        </div>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center mt-16">
          <motion.div {...fade(0.1)} className="order-2 lg:order-1"><CalendarPreview /></motion.div>
          <motion.div {...fade(0)} className="order-1 lg:order-2">
            <span className="text-xs uppercase tracking-widest" style={{ color: C.gold }}>Previsione domanda</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mt-3" style={SERIF}>Un prezzo giusto per ogni notte, 90 giorni avanti</h2>
            <p className="mt-4 leading-relaxed" style={{ color: C.muted }}>Domanda, prezzo consigliato, minimo e massimo per ogni giorno. Il calendario intelligente ti fa vedere subito le date calde da valorizzare.</p>
          </motion.div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <motion.h2 {...fade(0)} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center" style={SERIF}>Tutto ciò che serve per decidere meglio</motion.h2>
        <motion.p {...fade(0.08)} className="mt-4 text-center max-w-2xl mx-auto" style={{ color: C.muted }}>Non solo competitor: Revenue AI incrocia meteo, eventi locali, il tuo storico e i trend di mercato per suggerirti la tariffa giusta.</motion.p>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={i} {...fade((i % 3) * 0.08)} className="rounded-2xl p-6 border" style={{ background: C.surface, borderColor: C.border }} data-testid={`feature-${i}`}>
              <f.Icon className="w-7 h-7 mb-4" style={{ color: C.gold }} />
              <h3 className="text-lg font-semibold mb-2">{f.t}</h3>
              <p className="text-sm leading-relaxed" style={{ color: C.muted }}>{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Spotlight distinctive features */}
      <section className="max-w-7xl mx-auto px-6 pb-4">
        <div className="grid md:grid-cols-2 gap-6">
          {SPOTLIGHT.map((s, i) => (
            <motion.div key={i} {...fade(i * 0.1)} className="rounded-3xl p-8 border relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1A1D28, #12141C)", borderColor: "rgba(229,169,60,0.25)" }} data-testid={`spotlight-${i}`}>
              <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: C.wine }}><s.Icon className="w-7 h-7" style={{ color: C.gold }} /></span>
              <h3 className="text-2xl font-bold mb-3" style={SERIF}>{s.t}</h3>
              <p className="leading-relaxed" style={{ color: C.muted }}>{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Tutor */}
      <section id="tutor" className="relative overflow-hidden py-24">
        <div className="absolute inset-0">
          <img src={TUTOR_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,11,14,0.97), rgba(88,17,26,0.55))" }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <motion.span {...fade(0)} className="text-xs uppercase tracking-widest" style={{ color: C.gold }}>Il tocco umano</motion.span>
          <motion.h2 {...fade(0.06)} className="text-3xl sm:text-4xl font-bold mt-3" style={SERIF}>L'Intelligenza Artificiale + la competenza umana</motion.h2>
          <motion.p {...fade(0.12)} className="mt-4 max-w-2xl mx-auto leading-relaxed" style={{ color: C.muted }}>Non sarai mai solo. Oltre al software e all'assistenza online, avrai 1 incontro mensile personalizzato con un tutor esperto di Revenue Management.</motion.p>
          <div className="mt-10 grid sm:grid-cols-3 gap-4 text-left">
            {[
              { Icon: MessageSquare, t: "1 sessione mensile 1-to-1", d: "Videochiamata dedicata alla tua struttura." },
              { Icon: Gauge, t: "Revisione strategia e prezzi", d: "Analisi delle metriche e piano d'azione su misura." },
              { Icon: ShieldCheck, t: "Assistenza online continua", d: "Supporto tecnico e strategico via chat/ticket." },
            ].map((x, i) => (
              <motion.div key={i} {...fade(0.15 + i * 0.08)} className="rounded-2xl p-6 border backdrop-blur-xl" style={{ background: "rgba(18,20,28,0.75)", borderColor: C.border }} data-testid={`tutor-${i}`}>
                <x.Icon className="w-6 h-6 mb-3" style={{ color: C.gold }} />
                <h3 className="font-semibold mb-1">{x.t}</h3>
                <p className="text-sm" style={{ color: C.muted }}>{x.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Roadmap PMS */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <motion.div {...fade(0)} className="rounded-3xl p-8 sm:p-10 border text-center" style={{ background: "linear-gradient(135deg, #1A1D28, #12141C)", borderColor: "rgba(229,169,60,0.25)" }} data-testid="roadmap-pms">
          <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: "rgba(229,169,60,0.15)", color: C.gold }}>In arrivo</span>
          <h2 className="text-2xl sm:text-3xl font-bold mt-4" style={SERIF}>Integrazione automatica con tutti i PMS</h2>
          <p className="mt-4 max-w-2xl mx-auto leading-relaxed" style={{ color: C.muted }}>
            Stiamo sviluppando i connettori diretti per aggiornare i prezzi sul tuo PMS in automatico (prezzi dinamici). E la notizia migliore? <span style={{ color: C.text }}>Chi entra ora tiene lo stesso prezzo per sempre.</span>
          </p>
          <div className="mt-6 flex items-center justify-center gap-3" style={{ color: C.gold }}>
            <PlugZap className="w-6 h-6" /><span className="text-sm font-semibold">Prezzi dinamici automatici, senza aumenti di prezzo</span>
          </div>
        </motion.div>
      </section>

      {/* Pricing */}
      <section id="prezzi" className="max-w-4xl mx-auto px-6 py-20">
        <motion.h2 {...fade(0)} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center" style={SERIF}>Un prezzo, tutto incluso</motion.h2>
        <motion.p {...fade(0.08)} className="mt-3 text-center" style={{ color: C.muted }}>Nessun costo di attivazione. Disdici quando vuoi.</motion.p>
        <motion.div {...fade(0.15)} className="mt-10 rounded-3xl p-[1px]" style={{ background: "linear-gradient(135deg, rgba(229,169,60,0.6), rgba(125,24,38,0.5))" }}>
          <div className="rounded-3xl p-8 sm:p-10" style={{ background: C.elevated }} data-testid="pricing-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: "rgba(125,24,38,0.35)", color: C.gold }}>★ Offerta limitata ai primi 50</span>
                <div className="mt-4 flex items-end gap-2">
                  <span className="text-5xl font-extrabold" style={SERIF}>79€</span>
                  <span className="mb-2" style={{ color: C.muted }}>/mese · bloccato a vita</span>
                </div>
                <p className="mt-1 text-sm" style={{ color: C.muted }}>Prezzo standard futuro: <span className="line-through">129€/mese</span></p>
              </div>
              <Button data-testid="pricing-request-access-btn" size="lg" onClick={go} className="rounded-full text-base px-8" style={{ background: C.gold, color: "#1A1204" }}>Blocca l'offerta</Button>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                "Prezzo di 79€/mese bloccato per sempre (anche con le future automazioni PMS)",
                "Abbonamento mensile flessibile: disdici quando vuoi, senza vincoli",
                "Tutor dedicato 1 volta al mese incluso",
                "Assistenza online continua inclusa",
                "Motore AI, intelligence competitor e previsioni a 90 giorni",
                "Nessun costo di attivazione nascosto",
              ].map((g, i) => (
                <div key={i} className="flex items-start gap-2 text-sm" style={{ color: C.text }} data-testid={`pricing-guarantee-${i}`}>
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: C.gold }} />{g}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-6 py-16">
        <motion.h2 {...fade(0)} className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-8" style={SERIF}>Domande frequenti</motion.h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} i={i} />)}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <motion.div {...fade(0)} className="rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden" style={{ background: `linear-gradient(120deg, ${C.wine}, ${C.wineLight})` }} data-testid="final-cta">
          <h2 className="text-3xl sm:text-4xl font-bold" style={SERIF}>Pronto a trasformare la gestione dei prezzi del tuo B&B?</h2>
          <p className="mt-4 max-w-2xl mx-auto" style={{ color: "rgba(248,250,252,0.85)" }}>Unisciti ai primi 50 gestori e assicurati la tariffa promozionale bloccata per sempre.</p>
          <Button data-testid="final-cta-request-access-btn" size="lg" onClick={go} className="mt-8 rounded-full text-base px-10" style={{ background: C.gold, color: "#1A1204" }}>Richiedi accesso ora</Button>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ borderColor: C.border }}>
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm" style={{ color: C.muted }}>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="Revenue AI" className="w-6 h-6 object-contain" draggable="false" />
            <span style={SERIF} className="font-bold text-base">Revenue<span style={{ color: C.gold }}>AI</span></span>
          </div>
          <span>© 2026 Revenue AI (revenueai.it) — Tutti i diritti riservati.</span>
          <button data-testid="footer-request-access" onClick={go} className="hover:text-white transition-colors">Richiedi accesso</button>
        </div>
      </footer>
    </div>
  );
}
